import axios from "axios";

const LONDON_COORDS = {
  latitude: 51.5072,
  longitude: -0.1276
};

const OPEN_METEO_API_URL = "https://api.open-meteo.com/v1/forecast";

const weatherCodeToSummary = (code) => {
  if (code === 0) return "Sunny";
  if ([1, 2].includes(code)) return "Partly Cloudy";
  if (code === 3) return "Cloudy";
  if ([45, 48].includes(code)) return "Foggy";
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) {
    return "Rain";
  }
  if ([71, 73, 75, 77, 85, 86].includes(code)) return "Snow";
  if ([95, 96, 99].includes(code)) return "Stormy";
  return "Partly Cloudy";
};

const weatherCodeToIcon = (code) => {
  if (code === 0) return "wb_sunny";
  if ([1, 2, 3].includes(code)) return "partly_cloudy_day";
  if ([45, 48].includes(code)) return "foggy";
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) {
    return "rainy";
  }
  if ([71, 73, 75, 77, 85, 86].includes(code)) return "ac_unit";
  if ([95, 96, 99].includes(code)) return "thunderstorm";
  return "partly_cloudy_day";
};

const weatherCodeToIconForHour = (code, hour24) => {
  const baseIcon = weatherCodeToIcon(code);
  const isNight = hour24 < 6 || hour24 >= 18;

  if (!isNight) {
    return baseIcon;
  }

  if (baseIcon === "wb_sunny") {
    return "nightlight";
  }

  if (baseIcon === "partly_cloudy_day") {
    return "partly_cloudy_night";
  }

  return baseIcon;
};

export const fetchLondonWeather = async () => {
  const data = await axios.get(
    `${OPEN_METEO_API_URL}?latitude=${LONDON_COORDS.latitude}&longitude=${LONDON_COORDS.longitude}&timezone=auto&daily=temperature_2m_min,temperature_2m_max,weathercode&hourly=temperature_2m,weathercode&forecast_days=7`
  );

  const daily = data?.data?.daily || {};
  const hourly = data?.data?.hourly || {};

  const dailyMin = daily?.temperature_2m_min || [];
  const dailyMax = daily?.temperature_2m_max || [];
  const dailyCode = daily?.weathercode || [];
  const dailyTime = daily?.time || [];

  const hourlyTemp = hourly?.temperature_2m || [];
  const hourlyCode = hourly?.weathercode || [];
  const hourlyTime = hourly?.time || [];

  if (
    !dailyMin.length ||
    !dailyMax.length ||
    !dailyCode.length ||
    !dailyTime.length ||
    !hourlyTemp.length ||
    !hourlyCode.length ||
    !hourlyTime.length
  ) {
    throw new Error("Weather data unavailable");
  }

  const todayTemp = Math.round((Number(dailyMax[0]) + Number(dailyMin[0])) / 2);

  const buildDayLabel = (dateStr, index) =>
    index === 0
      ? "Today"
      : new Date(`${dateStr}T00:00:00`).toLocaleDateString("en-GB", {
          weekday: "short"
        });

  const buildForecastItem = (dayIndex) => {
    const dateStr = dailyTime[dayIndex];
    const code = dailyCode[dayIndex];
    return {
      day: buildDayLabel(dateStr, dayIndex),
      icon: weatherCodeToIcon(code),
      summary: weatherCodeToSummary(code),
      high: `${Math.round(Number(dailyMax[dayIndex]))}°`,
      low: `${Math.round(Number(dailyMin[dayIndex]))}°`
    };
  };

  const forecast3 = Array.from({ length: Math.min(3, dailyTime.length) }).map(
    (_, idx) => buildForecastItem(idx)
  );

  const forecast7 = Array.from({ length: Math.min(7, dailyTime.length) }).map(
    (_, idx) => buildForecastItem(idx)
  );

  const hourly3h = [];
  const maxHours = Math.min(hourlyTime.length, 24);
  for (let i = 0; i < maxHours; i += 3) {
    const timeStr = hourlyTime[i];
    const code = hourlyCode[i];
    const temp = hourlyTemp[i];
    const d = new Date(timeStr);
    const timeLabel = d.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });

    // Extract hour from the API time string to avoid any local/browser TZ mismatch.
    const hour24 =
      typeof timeStr === "string" && timeStr.includes("T")
        ? Number(timeStr.slice(11, 13))
        : d.getHours();

    hourly3h.push({
      timeLabel,
      icon: weatherCodeToIconForHour(code, hour24),
      summary: weatherCodeToSummary(code),
      temp: `${Math.round(Number(temp))}°`
    });
  }

  return {
    location: "London",
    temperature: `${todayTemp}°`,
    condition: weatherCodeToSummary(dailyCode[0]),
    conditionIcon: weatherCodeToIcon(dailyCode[0]),
    high: `${Math.round(Number(dailyMax[0]))}°`,
    low: `${Math.round(Number(dailyMin[0]))}°`,
    wind: "N/A",
    humidity: "N/A",
    forecast: forecast3,
    forecast7,
    hourly3h
  };
};

export const fetchTubeStatus = async () => {
  const response = await axios.get(
    "https://api.tfl.gov.uk/Line/Mode/tube/Status"
  );
  const lines = response?.data || [];

  if (!lines.length) {
    throw new Error("Tube status unavailable");
  }

  // Prefer a consistent "headline" set of lines on the All Widgets page.
  // If Elizabeth isn't returned by the TfL API (or uses a different name), we fall back to Northern.
  const prioritizedQueries = ["Metropolitan", "Piccadilly", "Elizabeth", "Northern"];
  const findLineByQuery = (query) => {
    const q = String(query).toLowerCase();
    return lines.find((line) => {
      const name = String(line?.name || "");
      const n = name.toLowerCase();
      // Match exact, case-insensitive, or substring (e.g. "Elizabeth line").
      return n === q || n === q.replace(/\s+/g, " ") || n.includes(q);
    });
  };

  const prioritized = prioritizedQueries.map(findLineByQuery).filter(Boolean);

  const maxLines = 11;
  const alreadyIncludedNames = new Set(prioritized.map((l) => l.name));
  const rest = lines.filter((l) => !alreadyIncludedNames.has(l.name));
  const selected = [...prioritized, ...rest].slice(0, maxLines);

  return {
    updatedAt: "Live",
    lines: selected.map((line) => {
      const statusDescription =
        line.lineStatuses?.[0]?.statusSeverityDescription || "Good Service";
      const reason = line.lineStatuses?.[0]?.reason || "No reported issues.";
      const isGood = statusDescription.toLowerCase().includes("good");

      return {
        name: `${line.name} Line`,
        details: reason,
        status: statusDescription,
        state: isGood ? "good" : "alert"
      };
    })
  };
};
